import { useCallback, useEffect, useState } from 'react';
import ReactApexChart from 'react-apexcharts';
import api from '../services/ApiService';

// ── Helpers ───────────────────────────────────────────────────────────────────
const fmtDuration = (s) => {
  const sec = Math.round(Number(s) || 0);
  if (sec < 60)  return `${sec}s`;
  if (sec < 3600) return `${Math.floor(sec/60)}m ${sec%60}s`;
  return `${Math.floor(sec/3600)}h ${Math.floor((sec%3600)/60)}m`;
};

const today     = () => new Date().toISOString().split('T')[0];
const daysAgo   = (n) => { const d = new Date(); d.setDate(d.getDate()-n); return d.toISOString().split('T')[0]; };

const PURPLE = '#7C3AED';
const COLORS  = ['#7C3AED','#0EA5E9','#10B981','#F59E0B','#EF4444','#8B5CF6','#D946EF','#0891B2'];

function SummaryCard({ label, value, sub, color }) {
  return (
    <div style={{
      background: '#fff', borderRadius: 14, padding: '16px 20px',
      boxShadow: '0 1px 4px rgba(0,0,0,0.08)', flex: '1 1 140px', minWidth: 130,
    }}>
      <div style={{ fontSize: 12, color: '#6b7280', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</div>
      <div style={{ fontSize: 26, fontWeight: 700, color: color || '#1f2937', margin: '4px 0 2px' }}>{value ?? 0}</div>
      {sub && <div style={{ fontSize: 11, color: '#9ca3af' }}>{sub}</div>}
    </div>
  );
}

function FilterBar({ filters, setFilters, meta, onRefresh, onExport, loading }) {
  return (
    <div style={{ display:'flex', flexWrap:'wrap', gap:10, alignItems:'center', marginBottom:18 }}>
      <div style={{ display:'flex', gap:6, alignItems:'center' }}>
        <label style={{ fontSize:12, color:'#6b7280', fontWeight:600 }}>FROM</label>
        <input type="date" value={filters.from} max={filters.to}
          onChange={e => setFilters(f => ({ ...f, from: e.target.value }))}
          style={inputStyle} />
        <label style={{ fontSize:12, color:'#6b7280', fontWeight:600 }}>TO</label>
        <input type="date" value={filters.to} min={filters.from} max={today()}
          onChange={e => setFilters(f => ({ ...f, to: e.target.value }))}
          style={inputStyle} />
      </div>

      {meta?.queues?.length > 0 && (
        <select value={filters.queue_id || ''} onChange={e => setFilters(f => ({ ...f, queue_id: e.target.value || null }))} style={inputStyle}>
          <option value="">All Queues</option>
          {meta.queues.map(q => <option key={q.id} value={q.id}>{q.name}</option>)}
        </select>
      )}

      {meta?.agents?.length > 0 && (
        <select value={filters.agent_id || ''} onChange={e => setFilters(f => ({ ...f, agent_id: e.target.value || null }))} style={inputStyle}>
          <option value="">All Agents</option>
          {meta.agents.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
        </select>
      )}

      <button onClick={onRefresh} disabled={loading} style={btnStyle(PURPLE)}>
        {loading ? '...' : 'Apply'}
      </button>
      <button onClick={onExport} style={btnStyle('#6b7280')}>
        ↓ CSV
      </button>
    </div>
  );
}

const inputStyle = {
  background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8,
  padding: '5px 10px', fontSize: 13, color: '#374151', cursor: 'pointer',
};
const btnStyle = (bg) => ({
  background: bg, color: '#fff', border: 'none', borderRadius: 8,
  padding: '6px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer',
});

// ── Chart defaults ────────────────────────────────────────────────────────────
const baseChart = (type, extra = {}) => ({
  type,
  height: 300,
  toolbar: { show: false },
  ...extra,
});

const baseXAxis = (categories) => ({
  categories,
  labels: { style: { fontSize: '11px', colors: '#6b7280' }, rotate: -30 },
  axisBorder: { show: false },
  axisTicks: { show: false },
});

const baseYAxis = (label) => ({
  labels: { style: { fontSize: '11px', colors: '#6b7280' } },
  title: label ? { text: label, style: { fontSize: '11px', color: '#9ca3af' } } : undefined,
});

const baseStroke  = { curve: 'smooth', width: 2.5 };
const baseGrid    = { borderColor: '#f0f0f0', strokeDashArray: 4 };
const baseLegend  = { position: 'top', horizontalAlign: 'left', fontSize: '12px' };
const baseFill    = { type: 'gradient', gradient: { opacityFrom: 0.35, opacityTo: 0.05 } };

// ═══════════════════════════════════════════════════════════════════════════════
// 1. CALL VOLUME TRENDS
// ═══════════════════════════════════════════════════════════════════════════════
function CallVolumeReport({ meta, tenantId }) {
  const [filters, setFilters] = useState({ from: daysAgo(30), to: today(), queue_id: null, agent_id: null });
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await api.getReportCallVolume({ ...filters, view_tenant_id: tenantId });
      setData(d);
    } catch {} finally { setLoading(false); }
  }, [filters, tenantId]);

  useEffect(() => { load(); }, [tenantId]);

  const exportCSV = () => api.exportReport('call-volume', { ...filters, view_tenant_id: tenantId });

  const dates   = (data?.rows || []).map(r => r.date?.slice(5) || r.date);
  const series  = [
    { name: 'Inbound',   data: (data?.rows || []).map(r => +r.inbound_calls  || 0) },
    { name: 'Outbound',  data: (data?.rows || []).map(r => +r.outbound_calls  || 0) },
    { name: 'Missed',    data: (data?.rows || []).map(r => +r.missed_calls    || 0) },
    { name: 'Abandoned', data: (data?.rows || []).map(r => +r.abandoned_calls || 0) },
  ];

  const totals = (data?.rows || []).reduce((a, r) => ({
    total:     a.total     + (+r.total_calls     || 0),
    inbound:   a.inbound   + (+r.inbound_calls   || 0),
    outbound:  a.outbound  + (+r.outbound_calls  || 0),
    answered:  a.answered  + (+r.answered_calls  || 0),
    missed:    a.missed    + (+r.missed_calls     || 0),
    abandoned: a.abandoned + (+r.abandoned_calls  || 0),
  }), { total:0, inbound:0, outbound:0, answered:0, missed:0, abandoned:0 });

  const answerRate = totals.inbound > 0
    ? ((totals.answered / totals.inbound) * 100).toFixed(1)
    : 0;

  return (
    <div>
      <FilterBar filters={filters} setFilters={setFilters} meta={{ ...meta, agents: [] }} onRefresh={load} onExport={exportCSV} loading={loading} />

      <div style={{ display:'flex', flexWrap:'wrap', gap:12, marginBottom:20 }}>
        <SummaryCard label="Total Calls"   value={totals.total}     />
        <SummaryCard label="Inbound"       value={totals.inbound}   color="#7C3AED" />
        <SummaryCard label="Outbound"      value={totals.outbound}  color="#0EA5E9" />
        <SummaryCard label="Answered"      value={totals.answered}  color="#10B981" />
        <SummaryCard label="Missed"        value={totals.missed}    color="#F59E0B" />
        <SummaryCard label="Abandoned"     value={totals.abandoned} color="#EF4444" />
        <SummaryCard label="Answer Rate"   value={`${answerRate}%`} color={+answerRate >= 80 ? '#10B981' : '#EF4444'} />
      </div>

      {data?.rows?.length > 0 ? (
        <ReactApexChart
          options={{
            chart: baseChart('area'),
            xaxis: baseXAxis(dates),
            yaxis: baseYAxis('Calls'),
            stroke: baseStroke,
            fill:   baseFill,
            grid:   baseGrid,
            legend: baseLegend,
            colors: COLORS,
            dataLabels: { enabled: false },
            tooltip: { x: { show: true } },
          }}
          series={series}
          type="area" height={300}
        />
      ) : (
        <EmptyState loading={loading} />
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// 2. QUEUE PERFORMANCE
// ═══════════════════════════════════════════════════════════════════════════════
function QueuePerformanceReport({ meta, tenantId }) {
  const [filters, setFilters] = useState({ from: daysAgo(30), to: today(), queue_id: null });
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { setData(await api.getReportQueuePerformance({ ...filters, view_tenant_id: tenantId })); }
    catch {} finally { setLoading(false); }
  }, [filters, tenantId]);

  useEffect(() => { load(); }, [tenantId]);

  const exportCSV = () => api.exportReport('queue-performance', { ...filters, view_tenant_id: tenantId });

  const summary = data?.summary || [];
  const names   = summary.map(q => q.queue_name);
  const series  = [
    { name: 'Answered',  data: summary.map(q => +q.calls_answered  || 0) },
    { name: 'Abandoned', data: summary.map(q => +q.calls_abandoned || 0) },
    { name: 'Missed',    data: summary.map(q => +q.calls_missed    || 0) },
  ];

  return (
    <div>
      <FilterBar filters={filters} setFilters={setFilters} meta={{ queues: meta.queues, agents: [] }} onRefresh={load} onExport={exportCSV} loading={loading} />

      {summary.length > 0 && (
        <>
          <ReactApexChart
            options={{
              chart: { ...baseChart('bar'), stacked: true },
              xaxis: { ...baseXAxis(names), labels: { ...baseXAxis(names).labels, rotate: 0 } },
              yaxis: baseYAxis('Calls'),
              grid:   baseGrid,
              legend: baseLegend,
              colors: ['#10B981','#EF4444','#F59E0B'],
              plotOptions: { bar: { borderRadius: 4, columnWidth: '50%' } },
              dataLabels: { enabled: false },
            }}
            series={series}
            type="bar" height={300}
          />

          <table style={tableStyle}>
            <thead>
              <tr>{['Queue','Received','Answered','Abandoned','Missed','Avg Talk','SLA %'].map(h =>
                <th key={h} style={thStyle}>{h}</th>
              )}</tr>
            </thead>
            <tbody>
              {summary.map((q, i) => (
                <tr key={i} style={{ background: i%2===0 ? '#fafafa' : '#fff' }}>
                  <td style={tdStyle}>{q.queue_name}</td>
                  <td style={tdStyle}>{q.calls_received}</td>
                  <td style={{ ...tdStyle, color:'#10B981', fontWeight:600 }}>{q.calls_answered}</td>
                  <td style={{ ...tdStyle, color:'#EF4444' }}>{q.calls_abandoned}</td>
                  <td style={{ ...tdStyle, color:'#F59E0B' }}>{q.calls_missed}</td>
                  <td style={tdStyle}>{fmtDuration(q.avg_talk_time)}</td>
                  <td style={{ ...tdStyle, color: +q.sla_pct>=80 ? '#10B981' : '#EF4444', fontWeight:600 }}>{q.sla_pct}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
      {!summary.length && <EmptyState loading={loading} />}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// 3. SLA ANALYTICS
// ═══════════════════════════════════════════════════════════════════════════════
function SlaReport({ meta, tenantId }) {
  const [filters, setFilters] = useState({ from: daysAgo(30), to: today(), queue_id: null });
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { setData(await api.getReportSla({ ...filters, view_tenant_id: tenantId })); }
    catch {} finally { setLoading(false); }
  }, [filters, tenantId]);

  useEffect(() => { load(); }, [tenantId]);

  const exportCSV = () => api.exportReport('sla', { ...filters, view_tenant_id: tenantId });

  const overall  = data?.overall || {};
  const slaValue = parseFloat(overall.avg_sla_pct) || 0;

  const dates    = (data?.trend || []).map(r => r.date?.slice(5) || r.date);
  const slaLine  = (data?.trend || []).map(r => parseFloat(r.sla_pct) || 0);

  return (
    <div>
      <FilterBar filters={filters} setFilters={setFilters} meta={{ queues: meta.queues, agents: [] }} onRefresh={load} onExport={exportCSV} loading={loading} />

      <div style={{ display:'flex', flexWrap:'wrap', gap:20, marginBottom:20, alignItems:'flex-start' }}>
        {/* Gauge */}
        <div style={{ minWidth:220 }}>
          <ReactApexChart
            options={{
              chart: { type: 'radialBar', height: 220 },
              plotOptions: {
                radialBar: {
                  startAngle: -135, endAngle: 135,
                  hollow: { size: '60%' },
                  dataLabels: {
                    name: { offsetY: -10, fontSize: '14px', color: '#6b7280' },
                    value: { fontSize: '26px', fontWeight: 700, color: slaValue>=80?'#10B981':'#EF4444',
                      formatter: (v) => `${parseFloat(v).toFixed(1)}%` },
                  },
                  track: { background: '#f3f4f6' },
                },
              },
              fill: { colors: [slaValue>=80?'#10B981':'#EF4444'] },
              labels: ['Answer Rate'],
            }}
            series={[Math.min(100, slaValue)]}
            type="radialBar" height={220}
          />
        </div>

        <div style={{ display:'flex', flexWrap:'wrap', gap:12, flex:1 }}>
          <SummaryCard label="Total Inbound"  value={overall.total_calls}     />
          <SummaryCard label="Answered"       value={overall.answered_calls}   color="#10B981" />
          <SummaryCard label="Abandoned"      value={overall.abandoned_calls}  color="#EF4444" />
          <SummaryCard label="Avg Talk Time"  value={fmtDuration(overall.avg_talk_time)} />
          <SummaryCard label="Answer Rate"    value={`${slaValue.toFixed(1)}%`}
            color={slaValue>=80 ? '#10B981' : '#EF4444'} sub="Target: 80%" />
        </div>
      </div>

      {dates.length > 0 && (
        <ReactApexChart
          options={{
            chart: baseChart('line'),
            xaxis: baseXAxis(dates),
            yaxis: { ...baseYAxis('SLA %'), min: 0, max: 100 },
            stroke: { ...baseStroke, width: 3 },
            grid:   baseGrid,
            colors: [PURPLE],
            dataLabels: { enabled: false },
            annotations: {
              yaxis: [{ y: 80, borderColor: '#EF4444', strokeDashArray: 5,
                label: { text: 'Target 80%', style: { color:'#EF4444', fontSize:'11px' } } }],
            },
          }}
          series={[{ name: 'Answer Rate %', data: slaLine }]}
          type="line" height={260}
        />
      )}
      {!dates.length && <EmptyState loading={loading} />}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// 4. AGENT PERFORMANCE
// ═══════════════════════════════════════════════════════════════════════════════
function AgentPerformanceReport({ meta, tenantId }) {
  const [filters, setFilters] = useState({ from: daysAgo(30), to: today(), agent_id: null });
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { setData(await api.getReportAgentPerformance({ ...filters, view_tenant_id: tenantId })); }
    catch {} finally { setLoading(false); }
  }, [filters, tenantId]);

  useEffect(() => { load(); }, [tenantId]);

  const exportCSV = () => api.exportReport('agent-performance', { ...filters, view_tenant_id: tenantId });

  const lb = data?.leaderboard || [];

  return (
    <div>
      <FilterBar filters={filters} setFilters={setFilters} meta={{ queues: [], agents: meta.agents }} onRefresh={load} onExport={exportCSV} loading={loading} />

      {lb.length > 0 ? (
        <>
          <ReactApexChart
            options={{
              chart: baseChart('bar'),
              xaxis: { categories: lb.map(a => a.agent_name), labels: { style: { fontSize:'11px' } } },
              yaxis: baseYAxis('Calls Answered'),
              plotOptions: { bar: { horizontal: true, borderRadius: 5, barHeight: '55%' } },
              grid:   baseGrid,
              colors: [PURPLE],
              dataLabels: { enabled: true, style: { fontSize:'11px' } },
            }}
            series={[{ name: 'Calls Answered', data: lb.map(a => +a.calls_answered || 0) }]}
            type="bar" height={Math.max(200, lb.length * 36 + 60)}
          />

          <table style={{ ...tableStyle, marginTop: 16 }}>
            <thead>
              <tr>{['Agent','Answered','Missed','Outbound','Avg Talk','Total Talk','Login Time','Pause Time'].map(h =>
                <th key={h} style={thStyle}>{h}</th>
              )}</tr>
            </thead>
            <tbody>
              {lb.map((a, i) => (
                <tr key={i} style={{ background: i%2===0 ? '#fafafa' : '#fff' }}>
                  <td style={{ ...tdStyle, fontWeight:600, color:'#374151' }}>{a.agent_name}</td>
                  <td style={{ ...tdStyle, color:'#10B981', fontWeight:600 }}>{a.calls_answered}</td>
                  <td style={{ ...tdStyle, color:'#EF4444' }}>{a.calls_missed}</td>
                  <td style={tdStyle}>{a.calls_outbound}</td>
                  <td style={tdStyle}>{fmtDuration(a.avg_talk_time)}</td>
                  <td style={tdStyle}>{fmtDuration(a.total_talk_time)}</td>
                  <td style={tdStyle}>{fmtDuration(a.login_duration)}</td>
                  <td style={tdStyle}>{fmtDuration(a.pause_duration)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      ) : (
        <EmptyState loading={loading} />
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// 5. ABANDONED CALLS
// ═══════════════════════════════════════════════════════════════════════════════
function AbandonedCallsReport({ meta, tenantId }) {
  const [filters, setFilters] = useState({ from: daysAgo(30), to: today(), queue_id: null });
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { setData(await api.getReportAbandonedCalls({ ...filters, view_tenant_id: tenantId })); }
    catch {} finally { setLoading(false); }
  }, [filters, tenantId]);

  useEffect(() => { load(); }, [tenantId]);

  const exportCSV = () => api.exportReport('abandoned-calls', { ...filters, view_tenant_id: tenantId });

  const trend   = data?.trend || [];
  const hourly  = data?.hourly || [];
  const dates   = trend.map(r => r.date?.slice(5) || r.date);
  const hours   = Array.from({ length: 24 }, (_, i) => `${String(i).padStart(2,'0')}:00`);
  const hourMap = Object.fromEntries(hourly.map(h => [h.stat_hour, h]));

  const totals  = trend.reduce((a, r) => ({
    abandoned: a.abandoned + (+r.abandoned_calls || 0),
    inbound:   a.inbound   + (+r.inbound_calls   || 0),
  }), { abandoned: 0, inbound: 0 });

  const peakHour = hourly.reduce((a, h) => (+h.abandoned_calls||0) > (+a.abandoned_calls||0) ? h : a, { stat_hour: 0, abandoned_calls: 0 });

  return (
    <div>
      <FilterBar filters={filters} setFilters={setFilters} meta={{ queues: [], agents: [] }} onRefresh={load} onExport={exportCSV} loading={loading} />

      <div style={{ display:'flex', flexWrap:'wrap', gap:12, marginBottom:20 }}>
        <SummaryCard label="Abandoned"    value={totals.abandoned} color="#EF4444" />
        <SummaryCard label="Total Inbound" value={totals.inbound} />
        <SummaryCard label="Abandon Rate"
          value={totals.inbound > 0 ? `${((totals.abandoned/totals.inbound)*100).toFixed(1)}%` : '0%'}
          color="#EF4444" />
        <SummaryCard label="Peak Hour"    value={`${String(peakHour.stat_hour).padStart(2,'0')}:00`}
          sub={`${peakHour.abandoned_calls} abandoned`} />
      </div>

      {dates.length > 0 ? (
        <>
          <ReactApexChart
            options={{
              chart: baseChart('area'),
              xaxis: baseXAxis(dates),
              yaxis: baseYAxis('Abandoned Calls'),
              stroke: baseStroke,
              fill:   { type:'gradient', gradient:{ opacityFrom:0.4, opacityTo:0.02 } },
              grid:   baseGrid,
              colors: ['#EF4444'],
              dataLabels: { enabled: false },
            }}
            series={[{ name: 'Abandoned Calls', data: trend.map(r => +r.abandoned_calls||0) }]}
            type="area" height={260}
          />

          <div style={{ marginTop: 24 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 10 }}>Peak Abandonment by Hour</div>
            <ReactApexChart
              options={{
                chart: baseChart('bar'),
                xaxis: { categories: hours, labels: { style:{ fontSize:'10px' }, rotate:-45 } },
                yaxis: baseYAxis('Calls'),
                plotOptions: { bar: { borderRadius: 3, columnWidth:'70%' } },
                grid: baseGrid,
                colors: ['#F87171'],
                dataLabels: { enabled: false },
              }}
              series={[{ name: 'Abandoned', data: hours.map((_, i) => +(hourMap[i]?.abandoned_calls||0)) }]}
              type="bar" height={220}
            />
          </div>
        </>
      ) : (
        <EmptyState loading={loading} />
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// 6. MISSED CALLS
// ═══════════════════════════════════════════════════════════════════════════════
function MissedCallsReport({ meta, tenantId }) {
  const [filters, setFilters] = useState({ from: daysAgo(30), to: today(), agent_id: null });
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { setData(await api.getReportMissedCalls({ ...filters, view_tenant_id: tenantId })); }
    catch {} finally { setLoading(false); }
  }, [filters, tenantId]);

  useEffect(() => { load(); }, [tenantId]);

  const exportCSV = () => api.exportReport('missed-calls', { ...filters, view_tenant_id: tenantId });

  const trend  = data?.trend || [];
  const byAgent = data?.byAgent || [];
  const dates  = trend.map(r => r.date?.slice(5) || r.date);

  const totals = trend.reduce((a, r) => ({
    missed:   a.missed   + (+r.missed_calls   || 0),
    answered: a.answered + (+r.answered_calls || 0),
  }), { missed: 0, answered: 0 });

  const missedRate = (totals.missed + totals.answered) > 0
    ? ((totals.missed / (totals.missed + totals.answered)) * 100).toFixed(1)
    : 0;

  return (
    <div>
      <FilterBar filters={filters} setFilters={setFilters} meta={{ queues: [], agents: meta.agents }} onRefresh={load} onExport={exportCSV} loading={loading} />

      <div style={{ display:'flex', flexWrap:'wrap', gap:12, marginBottom:20 }}>
        <SummaryCard label="Missed Calls"   value={totals.missed}   color="#EF4444" />
        <SummaryCard label="Answered"       value={totals.answered} color="#10B981" />
        <SummaryCard label="Missed Rate"    value={`${missedRate}%`} color={+missedRate>20?'#EF4444':'#F59E0B'} />
      </div>

      {dates.length > 0 ? (
        <>
          <ReactApexChart
            options={{
              chart: baseChart('line'),
              xaxis: baseXAxis(dates),
              yaxis: baseYAxis('Calls'),
              stroke: baseStroke,
              grid:   baseGrid,
              legend: baseLegend,
              colors: ['#EF4444','#10B981'],
              dataLabels: { enabled: false },
              markers: { size: 4 },
            }}
            series={[
              { name: 'Missed',   data: trend.map(r => +r.missed_calls   || 0) },
              { name: 'Answered', data: trend.map(r => +r.answered_calls || 0) },
            ]}
            type="line" height={260}
          />

          {byAgent.length > 0 && (
            <div style={{ marginTop: 24 }}>
              <div style={{ fontSize:13, fontWeight:600, color:'#374151', marginBottom:10 }}>Missed Calls by Agent</div>
              <table style={tableStyle}>
                <thead>
                  <tr>{['Agent','Missed','Answered','Miss Rate'].map(h =>
                    <th key={h} style={thStyle}>{h}</th>
                  )}</tr>
                </thead>
                <tbody>
                  {byAgent.map((a, i) => {
                    const total = (+a.missed_calls||0) + (+a.answered_calls||0);
                    const rate  = total > 0 ? ((+a.missed_calls||0)/total*100).toFixed(1) : '0.0';
                    return (
                      <tr key={i} style={{ background: i%2===0 ? '#fafafa' : '#fff' }}>
                        <td style={{ ...tdStyle, fontWeight:600 }}>{a.agent_name}</td>
                        <td style={{ ...tdStyle, color:'#EF4444', fontWeight:600 }}>{a.missed_calls}</td>
                        <td style={{ ...tdStyle, color:'#10B981' }}>{a.answered_calls}</td>
                        <td style={{ ...tdStyle, color: +rate>20?'#EF4444':'#6b7280' }}>{rate}%</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      ) : (
        <EmptyState loading={loading} />
      )}
    </div>
  );
}

// ── Empty state ───────────────────────────────────────────────────────────────
function EmptyState({ loading }) {
  return (
    <div style={{ textAlign:'center', padding:'60px 20px', color:'#9ca3af' }}>
      {loading ? (
        <div style={{ fontSize:14 }}>Loading data...</div>
      ) : (
        <>
          <div style={{ fontSize:36, marginBottom:8 }}>📊</div>
          <div style={{ fontSize:14, fontWeight:600 }}>No data for this period</div>
          <div style={{ fontSize:12, marginTop:4 }}>Try adjusting the date range or run an aggregation</div>
        </>
      )}
    </div>
  );
}

// ── Table styles ──────────────────────────────────────────────────────────────
const tableStyle = {
  width: '100%', borderCollapse: 'collapse', fontSize: 13, marginTop: 8,
  border: '1px solid #f0f0f0', borderRadius: 8, overflow: 'hidden',
};
const thStyle = {
  background: '#f9fafb', padding: '8px 12px', textAlign: 'left',
  fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.04em',
};
const tdStyle = { padding: '8px 12px', color: '#374151', borderTop: '1px solid #f3f4f6' };

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN REPORTS PAGE
// ═══════════════════════════════════════════════════════════════════════════════
const REPORTS = [
  { id: 'call-volume',       label: 'Call Volume',       icon: '📈', component: CallVolumeReport },
  { id: 'queue-performance', label: 'Queue Performance', icon: '☰',  component: QueuePerformanceReport },
  { id: 'sla',               label: 'SLA Analytics',     icon: '🎯', component: SlaReport },
  { id: 'agent-performance', label: 'Agent Performance', icon: '👤', component: AgentPerformanceReport },
  { id: 'abandoned-calls',   label: 'Abandoned Calls',   icon: '📵', component: AbandonedCallsReport },
  { id: 'missed-calls',      label: 'Missed Calls',      icon: '⚠',  component: MissedCallsReport },
];

export default function ReportsPage({ adminTenantOverride } = {}) {
  const [activeReport, setActiveReport] = useState('call-volume');
  const [meta, setMeta]                 = useState({ queues: [], agents: [], tenants: [] });
  const [tenantId, setTenantId]         = useState(adminTenantOverride || null);
  const [aggregating, setAggregating]   = useState(false);
  const [aggMsg, setAggMsg]             = useState('');
  const isSuperAdmin                    = adminTenantOverride === undefined;

  useEffect(() => {
    api.getReportsMeta(tenantId).then(d => {
      setMeta(d);
      // For super admins with no tenant selected, pick first available
      if (isSuperAdmin && !tenantId && d.tenants?.length > 0) {
        setTenantId(d.tenants[0].id);
      }
    }).catch(() => {});
  }, [tenantId]);

  const triggerAggregation = async () => {
    setAggregating(true);
    setAggMsg('');
    try {
      await api.triggerAggregation(null, tenantId);
      setAggMsg('Aggregation complete! Refresh a report to see updated data.');
    } catch (e) {
      setAggMsg('Aggregation failed: ' + e.message);
    } finally {
      setAggregating(false);
    }
  };

  const Active = REPORTS.find(r => r.id === activeReport)?.component;

  return (
    <div style={{ display: 'flex', minHeight: '100%' }}>
      {/* Left nav */}
      <div style={{
        width: 200, flexShrink: 0, background: '#fff', borderRight: '1px solid #f0f0f0',
        display: 'flex', flexDirection: 'column', padding: '8px 0', alignSelf: 'flex-start',
        position: 'sticky', top: 0,
      }}>
        <div style={{ padding: '12px 16px 8px', fontSize: 11, fontWeight: 700, color: '#9ca3af', textTransform:'uppercase', letterSpacing:'0.05em' }}>
          Reports
        </div>
        {REPORTS.map(r => (
          <button key={r.id} onClick={() => setActiveReport(r.id)}
            style={{
              display: 'flex', alignItems: 'center', gap: 8,
              padding: '9px 16px', border: 'none', background: 'none', cursor: 'pointer',
              textAlign: 'left', fontSize: 13, fontWeight: activeReport===r.id ? 600 : 400,
              color: activeReport===r.id ? PURPLE : '#374151',
              borderRight: activeReport===r.id ? `3px solid ${PURPLE}` : '3px solid transparent',
              borderRadius: 0,
            }}>
            <span style={{ fontSize: 15 }}>{r.icon}</span>
            {r.label}
          </button>
        ))}

        <div style={{ flex: 1 }} />

        {/* Tenant selector — only for super admins */}
        {isSuperAdmin && meta.tenants?.length > 0 && (
          <div style={{ padding: '10px 14px', borderTop: '1px solid #f0f0f0' }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#9ca3af', marginBottom: 5, textTransform: 'uppercase' }}>Tenant</div>
            <select value={tenantId || ''} onChange={e => setTenantId(e.target.value ? Number(e.target.value) : null)}
              style={{ ...inputStyle, width: '100%', fontSize: 12 }}>
              {meta.tenants.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>
        )}

        <div style={{ padding: '10px 14px', borderTop: '1px solid #f0f0f0' }}>
          <button onClick={triggerAggregation} disabled={aggregating}
            style={{ ...btnStyle(aggregating ? '#9ca3af' : '#10B981'), width:'100%', fontSize:12, padding:'7px 10px' }}>
            {aggregating ? 'Running...' : '⟳ Refresh Stats'}
          </button>
          {aggMsg && <div style={{ fontSize:11, color: aggMsg.includes('fail')?'#EF4444':'#10B981', marginTop:6, lineHeight:1.3 }}>{aggMsg}</div>}
        </div>
      </div>

      {/* Report content */}
      <div style={{ flex: 1, padding: 24, minWidth: 0 }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
          <div>
            <h2 style={{ margin:0, fontSize:20, fontWeight:700, color:'#1f2937' }}>
              {REPORTS.find(r => r.id===activeReport)?.icon} {REPORTS.find(r => r.id===activeReport)?.label}
            </h2>
            <div style={{ fontSize:12, color:'#9ca3af', marginTop:2 }}>
              Historical analytics — refreshed hourly
              {isSuperAdmin && meta.tenants?.find(t => t.id === tenantId) &&
                <span style={{ marginLeft:8, color: PURPLE, fontWeight:600 }}>
                  · {meta.tenants.find(t => t.id === tenantId)?.name}
                </span>
              }
            </div>
          </div>
        </div>

        {Active && <Active meta={meta} tenantId={tenantId} />}
      </div>
    </div>
  );
}
